import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin, Observable, of } from "rxjs";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { ShiftType } from "src/app/enums/shiftTypeValue.enum";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { SegOrgLevel } from "src/app/models/segOrgLevel.model";
import { Shift } from "src/app/models/shift.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { SegOrgLevelService } from "src/app/services/seg-org-level.service";
import { SegService } from "src/app/services/seg.service";
import { ShiftService } from "src/app/services/shift.service";
import { TypeService } from "src/app/services/type.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    FormComponent,
    IFormDialogData,
    IOrganizationLevelEntity,
} from "src/app/shared/form/form.component";
import {
    HierarchicalSelectionFormField,
    NumberFormField,
    SingleSelectionFormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";

export interface ISegOrgLevelDialogData
    extends IFormDialogData, IOrganizationLevelEntity {
    segId: string;
}

/**
 * DASH-3187: single-org-level "Add To My Site" form for a SINGLE SEG.
 *
 * This is the row-level entry point restored from the original SegSiteDialog
 * (prior revision at 5a4112abd^), extracted into its own component so the bulk
 * scope-matrix + entry-grid stepper (now SegSiteDialogComponent, delivered by
 * DASH-2986 / DASH-3152) stays reachable ONLY from the bulk / multi-select
 * entry points and is left completely untouched.
 *
 * Fields are the seven original single-form fields plus a new Shift (shiftId)
 * selector showing every shift accessible to the current user (server-scoped to
 * the user's org-level hierarchy by the GetList endpoint). The backend resolves
 * the selected shift by Id only — there is no org-level exact-match requirement.
 * Selecting a Shift derives shiftType/shiftLengthHours/avgWeeklyHours from that
 * Shift record (PATH A); clearing it resets those three (PATH B).
 */
@Component({
  standalone: false,
    selector: "app-seg-org-level-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./../../shared/form/form.component.scss"],
})
export class SegOrgLevelDialogComponent
    extends FormComponent<SegOrgLevel>
    implements OnInit
{
    organizationLevels: OrganizationLevel[];
    segs: ILookup[];
    private allShifts: Shift[] = [];

    constructor(
        dialogRef: MatDialogRef<SegOrgLevelDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ISegOrgLevelDialogData,
        protected segOrgLevelService: SegOrgLevelService,
        protected segService: SegService,
        private organizationLevelService: OrganizationLevelService,
        private shiftService: ShiftService,
        protected typeService: TypeService,
        protected injector: Injector,
    ) {
        super(
            dialogRef,
            segOrgLevelService,
            data,
            "SEG to Site",
            injector,
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            organizationLevels: this.organizationLevelService.getAll(),
            segs: this.segService.getAll(),
            shifts: this.shiftService.getAllEntities(),
        }).subscribe({
            next: (result) => {
                this.entity = {
                    ...result.entity,
                    segId: this.data.segId,
                    organizationLevelId: this.data.organizationLevelId,
                };
                this.organizationLevels = result.organizationLevels;
                this.segs = result.segs;
                this.allShifts = result.shifts ?? [];
                this.initFields();
                this.isFormLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
    }

    getEntity(): Observable<SegOrgLevel> {
        return this.isAdd
            ? of(this.getNewObject())
            : this.segOrgLevelService.getBySegId(
                  this.data.segId,
                  this.data.organizationLevelId,
              );
    }

    getNewObject(): SegOrgLevel {
        return new SegOrgLevel();
    }

    validate(): boolean {
        return true;
    }

    // DASH-3187 fix: the Shift lookup lists the shifts returned by
    // ShiftService.getAllEntities() (the GetList grid endpoint). That endpoint
    // already scopes rows server-side to the org levels the caller can access
    // (OrgLevelSecurityService.ApplyOrgLevelSecurity over the user's org-level
    // hierarchy), so the list is appropriately bounded to the tenant/user.
    //
    // We deliberately DO NOT additionally filter client-side to shifts whose
    // organizationLevelId exactly equals the selected org level. That extra
    // exact-single-level filter produced an EMPTY dropdown even when the API
    // returned shifts (reported by Tandeep) whenever a tenant defines its
    // shifts at a different org level than the one the SEG is being added to
    // (e.g. shifts kept at a Site while the SEG is added at a Department under
    // it). The backend does not require an exact match either: SegOrgLevel
    // Create/Update (CopyShiftValuesIntoDtoAsync) resolves the shift by Id only
    // and never validates its organizationLevelId, so any accessible shift is a
    // valid link. Showing every accessible shift is therefore both correct and
    // what makes the reported empty dropdown populate.
    private getShiftOptions(): ILookup[] {
        return this.allShifts.map((shift) => ({
            id: shift.id,
            name: shift.name,
        }));
    }

    // PATH A (DASH-3187): a Shift belongs to an org level and carries the
    // canonical roster values, so selecting a Shift derives and persists
    // shiftType, shiftLengthHours and avgWeeklyHours from that Shift record.
    // Clearing the Shift (PATH B) clears the derived fields so the user can
    // enter them manually. This prevents a NULL ShiftType silently resolving
    // to enum 0 and corrupting the OEL. allShifts already holds the full
    // ShiftDto, so no extra request is needed.
    private onShiftChange(): void {
        const shift = this.allShifts.find(
            (candidate) => candidate.id === this.entity.shiftId,
        );
        if (shift) {
            this.entity.shiftType = shift.shiftType;
            this.entity.shiftLengthHours = shift.shiftLengthHours;
            this.entity.avgWeeklyHours = shift.averageWeeklyHours;
        } else {
            this.entity.shiftType = null;
            this.entity.shiftLengthHours = null;
            this.entity.avgWeeklyHours = null;
        }
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<SegOrgLevel>((x) => x.segId),
                options: this.segs,
                name: "SEG Name",
                columnSpan: new ColumnSpan(1, 1),
                readonly: true,
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<SegOrgLevel>((x) => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: "Organization Level",
                columnSpan: new ColumnSpan(1, 1),
                required: true,
                readonly: !this.isAdd,
            }),
            new SingleSelectionFormField({
                property: nameOf<SegOrgLevel>((x) => x.shiftId),
                options: this.getShiftOptions(),
                name: "Shift",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
                onChange: () => this.onShiftChange(),
            }),
            new SingleSelectionFormField({
                property: nameOf<SegOrgLevel>((x) => x.shiftType),
                options: this.typeService
                    .getEnumOptions(ShiftType)
                    .map((res) => ({
                        id: res.id,
                        name: this.translateService.getDynamicTranslation(
                            res.name.replace("_", " "),
                        ),
                    })),
                name: "Shift Type",
                columnSpan: new ColumnSpan(1, 1),
                required: false,
            }),
            new NumberFormField({
                property: nameOf<SegOrgLevel>((x) => x.shiftLengthHours),
                name: "Shift Length (hours)",
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new NumberFormField({
                property: nameOf<SegOrgLevel>((x) => x.avgWeeklyHours),
                name: "Average weekly Hours",
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new NumberFormField({
                property: nameOf<SegOrgLevel>((x) => x.numberOfWorkers),
                name: "Number of Employees",
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
            new NumberFormField({
                property: nameOf<SegOrgLevel>((x) => x.numberOfContractors),
                name: "Number of Contractors",
                columnSpan: new ColumnSpan(1, 2),
                required: false,
            }),
        ];
    }
}
